import { computeSessionBadges, type GameSummary } from '../../game/summary';
import type { Game } from '../../game/types';
import { DEFAULT_WINNER_MESSAGE, readBranding, renderBrandHeader } from '../branding';
import { launchConfetti } from '../confetti';
import {
  deltaSpan,
  esc,
  formatGameTimestamp,
  formatRoundListItem,
  formatWinnerBannerText,
  playerName,
} from '../helpers';
import {
  canShareAnything,
  prepareResultsPdf,
  shareButtonLabel,
  wireResultsDownload,
  wireResultsShare,
} from '../resultsShare';
import { expandAllSettlementExplanations } from '../settlementExplain';
import { buildPlayerPosters } from '../../game/posters';
import type { SubmissionStatus } from '../leaderboardSubmit';
import { offerOwnPoster, posterFooter, postersCardHtml, wirePosters } from './posters';
import { gameViewerKey, getViewer } from '../viewerIdentity';
import { paintLeaderboardStatus, type LeaderboardStatusActions } from './leaderboardStatus';
import { updateMarker } from '../updateSafety';

export interface SummaryActions {
  newGame(): void;
  /** Undoes an End Game tapped by mistake — back to this same game, rounds already recorded intact. */
  resumeGame(): void;
  /** Omit only when this screen is itself being shown read-only from Past Games (see `historyBack`) — there's no reason to browse history from inside history. */
  viewPastGames?(): void;
  /** Saves this one game as a file to keep elsewhere (host device only); resolves with the line to show. Omit on any screen that isn't the host's own copy. */
  saveGameFile?(): Promise<string>;
  /** Shown when this game was shared: when the guests' links stop working. Omit for a game that was never shared. */
  guestLinkNote?: string;
  /** Present only for a Leaderboard game: draws the "sent to the leaderboard" card and wires its buttons. */
  leaderboard?: LeaderboardStatusActions & { status(): SubmissionStatus };
}

/** Suggests a sensible filename when the browser's print dialog offers "Save as PDF". */
function printableTitle(game: Game): string {
  const date = new Date(game.endedAt ?? Date.now()).toISOString().slice(0, 10);
  return `Mahjong ${game.tableId} — ${date}`;
}

export function renderSummary(
  root: HTMLElement,
  game: Game,
  summary: GameSummary,
  actions: SummaryActions,
  /** Set only when reopened read-only from Past Games — swaps "New Game"/"go back to this game" for a single "Back to Past Games", since this isn't the device's current game. */
  historyBack?: () => void,
): void {
  const posters = buildPlayerPosters(game, summary);
  const rows = summary.players
    .map(
      (p) => `
      <tr class="${p.rank === 1 ? 'winner' : ''}">
        <td>${p.rank}</td>
        <td>${esc(p.name)}</td>
        <td>${deltaSpan(p.totalScore)}</td>
        <td>${p.roundsWon}</td>
        <td>${p.selfDraws}</td>
        <td>${p.timesDiscardedInto}</td>
      </tr>
    `,
    )
    .join('');

  const winnerNames = summary.winners.map((id) => playerName(game, id)).join(', ');

  // Skipped for a game ended with zero rounds played — every player ties at
  // 0 with nothing to distinguish them, so `winners` ends up being all 4
  // players, and a "🎉 Congrats, everyone!" fanfare for a game where
  // nothing happened reads as a bug, not a celebration.
  const winnerBanner =
    summary.roundsPlayed > 0 && summary.winners.length > 0
      ? (() => {
          const text = formatWinnerBannerText(readBranding().winnerMessage ?? DEFAULT_WINNER_MESSAGE, winnerNames);
          return `
      <div class="winner-banner">
        <div class="winner-banner-track">
          <span>${text}</span>
          <span>${text}</span>
        </div>
      </div>`;
        })()
      : '';

  const roundRows = summary.rounds.map((r) => formatRoundListItem(game, r)).join('');

  const badges = computeSessionBadges(game, summary);
  const badgesSection =
    badges.length > 0
      ? `
      <div class="session-badges">
        ${badges
          .map(
            (b) => `
            <div class="badge-chip">
              <div class="badge-label">${esc(b.label)}</div>
              <div class="badge-detail">${esc(b.detail)}</div>
            </div>`,
          )
          .join('')}
      </div>`
      : '';

  // navigator.share isn't available on every browser (notably desktop
  // Chrome) — omit the button there rather than show one that would always
  // fail, same feature-detect-and-hide approach as this app already takes
  // for navigator.clipboard in shareGame.ts. Says "Share PDF" where the PDF
  // itself can be shared as a file, plain "Share" where only text can.
  const shareBtn = canShareAnything()
    ? `<button id="shareResultsBtn" class="secondary" type="button">${shareButtonLabel()}</button>`
    : '';

  const saveFileBtn = actions.saveGameFile
    ? `<button id="saveGameFileBtn" class="ghost" type="button" style="width:100%;margin-top:0.5rem">💾 Save This Game to a File</button>
       <p id="saveGameFileMsg" class="muted-on-page" role="status" hidden style="margin:0.5rem 0 0"></p>`
    : '';

  const actionButtons = historyBack
    ? `
      <div class="row">
        <button id="printBtn" class="secondary" type="button">Download as PDF</button>
        ${shareBtn}
      </div>
      ${saveFileBtn}
      <button id="pastGamesBackBtn" class="ghost" type="button" style="width:100%;margin-top:0.5rem">
        ← Back to Past Games
      </button>`
    : `
      <div class="row">
        <button id="printBtn" class="secondary" type="button">Download as PDF</button>
        ${shareBtn}
        <button id="newGameBtn" type="button">New Game</button>
      </div>
      ${
        actions.viewPastGames
          ? `<button id="pastGamesBtn" class="ghost" type="button" style="width:100%;margin-top:0.5rem">Past Games</button>`
          : ''
      }
      ${saveFileBtn}
      <button id="resumeGameBtn" class="ghost" type="button" style="width:100%;margin-top:0.5rem">
        Ended this by mistake? Go back to this game
      </button>`;

  root.innerHTML = `
    ${renderBrandHeader()}
    ${updateMarker('ok')}
    <p class="muted-on-page" style="margin:0 0 0.5rem">
      Game started: ${formatGameTimestamp(game.startedAt)}<br />
      Game ended: ${game.endedAt ? formatGameTimestamp(game.endedAt) : '—'}
    </p>
    ${actions.guestLinkNote ? `<p class="muted-on-page" id="guestLinkNote" style="margin:0 0 0.5rem">${esc(actions.guestLinkNote)}</p>` : ''}
    ${winnerBanner}
    <div class="card">
      <h1>Final Results</h1>
      <p class="muted">Table ${esc(game.tableId)} · ${summary.roundsPlayed} round${summary.roundsPlayed === 1 ? '' : 's'} played</p>
      <p>${summary.winners.length > 1 ? 'Tied' : 'Winner'}: <strong>${esc(winnerNames)}</strong></p>

      <table class="summary">
        <thead>
          <tr><th>#</th><th>Player</th><th>Score</th><th>Rounds won</th><th>Self-draws</th><th>Fed a win</th></tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>
      <p class="muted summary-scroll-hint">Swipe the table sideways to see every column.</p>
      <p class="muted" style="margin-top:0.5rem">Ties on total score are broken by rounds won, then by whoever reached that score first.</p>

      ${badgesSection}
    </div>

    ${postersCardHtml(posters)}

    ${actions.leaderboard ? '<div class="card" id="leaderboardStatus"></div>' : ''}

    ${actionButtons}

    <div class="card">
      <h2>Round by round</h2>
      <ul class="round-list">${roundRows || '<li class="muted">No rounds played.</li>'}</ul>
    </div>
  `;

  // The screen this replaces (usually the play screen, scrolled down to its
  // End Game button) leaves the page scrolled part-way — start at the top,
  // where the winner banner and ranking table are.
  window.scrollTo(0, 0);

  // Only for a freshly-ended game, not every time a past game is reopened
  // read-only from history — otherwise browsing old results would re-launch
  // confetti on every visit, which reads as a bug, not a celebration.
  if (winnerBanner && !historyBack) {
    launchConfetti();
  }

  // Download as PDF and Share send the very same generated PDF (built once,
  // in the background, as soon as this screen renders). The browser's print
  // dialog — what Download used to always open, with a different layout — is
  // kept only as the fallback if that PDF can't be built.
  const pdf = prepareResultsPdf(game, summary, readBranding());

  wireResultsDownload(root.querySelector<HTMLButtonElement>('#printBtn')!, pdf, () => {
    const previousTitle = document.title;
    document.title = printableTitle(game);
    expandAllSettlementExplanations();
    window.print();
    document.title = previousTitle;
  });

  const shareResultsBtn = root.querySelector<HTMLButtonElement>('#shareResultsBtn');
  if (shareResultsBtn) wireResultsShare(shareResultsBtn, game, summary, pdf);

  wirePosters(root, posters, readBranding(), posterFooter(game.tableId, game.endedAt));
  // A leaderboard game from a sign-in table knows which player this phone is: offer that player their own poster.
  // Not when the results are only being looked back at from Past Games (the confetti does not come back then either).
  if (game.endedAt && !historyBack) {
    offerOwnPoster(posters, { key: gameViewerKey(game.id), nickname: getViewer(gameViewerKey(game.id)) }, readBranding(), posterFooter(game.tableId, game.endedAt));
  }

  if (actions.leaderboard) paintLeaderboardStatus(root, game, actions.leaderboard.status(), actions.leaderboard);

  const saveGameFileBtn = root.querySelector<HTMLButtonElement>('#saveGameFileBtn');
  if (saveGameFileBtn && actions.saveGameFile) {
    const saveMsg = root.querySelector<HTMLParagraphElement>('#saveGameFileMsg')!;
    const save = actions.saveGameFile;
    saveGameFileBtn.addEventListener('click', () => {
      saveGameFileBtn.disabled = true;
      void save()
        .then((line) => {
          saveMsg.textContent = line;
        })
        .catch(() => {
          saveMsg.textContent = "The file couldn't be saved. Try again.";
        })
        .finally(() => {
          saveMsg.hidden = false;
          saveGameFileBtn.disabled = false;
        });
    });
  }

  if (historyBack) {
    root.querySelector<HTMLButtonElement>('#pastGamesBackBtn')!.addEventListener('click', historyBack);
    return;
  }

  root.querySelector<HTMLButtonElement>('#newGameBtn')!.addEventListener('click', () => {
    actions.newGame();
  });

  root.querySelector<HTMLButtonElement>('#pastGamesBtn')?.addEventListener('click', () => {
    actions.viewPastGames?.();
  });

  root.querySelector<HTMLButtonElement>('#resumeGameBtn')!.addEventListener('click', () => {
    actions.resumeGame();
  });
}
